import { FC } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { BreadCrumbItem, Breadcrumb } from 'src/Components/Breadcrumb'
import { NAVIGATE_BACK_INDEX } from 'src/Constants/constants'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { useGetMetaAdsCampaignDetailsQuery } from 'src/Redux/metaAdsDetailsApi'
import { PrimarySkeleton } from 'src/Components/Skeletons/PrimarySkeleton'
import { useCampaignPayment } from 'src/Hooks/useCampaignPayment'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

import { MetaAdsContent } from './MetaAdsContent'
import { PerformanceOverview } from './MetaAdsContent/PerformanceOverview'
import { useTypeformForMetaAds } from './MetaAdsContent/TypeformMetaAds/useTypeformForMetaAds'
import { InfoBlock } from './MetaAdsContent/InfoBlock'
import { MetaAdsAutoRenewBlock } from './MetaAdsContent/MetaAdsAutoRenewBlock'
import { useChart } from './MetaAdsContent/useChart'
import {
  getMetaAdsVisibilityFlags,
  getMetaAdsAutoRenewVisibility,
} from './helpers'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    gap: 48px;
    padding-bottom: 104px;
  }
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 16px;
  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    gap: 48px;
  }
`
const ContentContainer = styled.div`
  display: flex;
  max-width: 1120px;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    flex-direction: column;
    gap: 48px;
  }
`
const StyledSkeleton = styled(PrimarySkeleton)`
  border-radius: 16px;
  height: 120px;
`

const AutoRenewOffWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 1120px;
  align-self: stretch;
`

export const MetaAdsCampaignDetailsPage: FC = () => {
  useTypeformForMetaAds()
  const navigate = useNavigate()
  const { campaignID } = useParams<{ campaignID: string }>()

  const { data, isLoading } = useGetMetaAdsCampaignDetailsQuery(
    campaignID || '',
    {
      skip: !campaignID,
    },
  )

  const payment = useCampaignPayment(
    CampaignPlatformTypeEnum.META,
    Number(campaignID) || void 0,
  )

  const items: BreadCrumbItem[] = [
    {
      title: 'Campaigns',
      path: ArtistRoutesEnum.CampaignsList,
    },
    {
      title: 'Campaign Details',
      path: '',
    },
  ]
  const handleButtonClick = (): void => {
    navigate(NAVIGATE_BACK_INDEX)
  }
  const onStartCampaignClick = (): void => {
    navigate(`${ArtistRoutesEnum.AdsSchedule}/${campaignID}`)
  }

  const { chartData } = useChart({
    campaignId: String(campaignID) || '0',
    startDate: data?.startDate,
    endDate: data?.endDate,
  })

  const { isInfoBlockVisible, isPerformanceOverviewVisible, isChartVisible } =
    getMetaAdsVisibilityFlags(data?.status, chartData)

  const { isAutoRenewOffVisible } = getMetaAdsAutoRenewVisibility(
    data?.status,
    data?.shouldAutoRenew,
  )

  if (isLoading) {
    return (
      <PageContainer>
        <Breadcrumb items={items} onButtonClick={handleButtonClick} />
        <ContentContainer>
          <StyledSkeleton />
        </ContentContainer>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Breadcrumb items={items} onButtonClick={handleButtonClick} />
      {isAutoRenewOffVisible && (
        <AutoRenewOffWrapper>
          <MetaAdsAutoRenewBlock
            campaignId={data?.id}
            shouldAutoRenew={data?.shouldAutoRenew}
            endDate={data?.endDate}
            daysCount={data?.daysCount}
          />
        </AutoRenewOffWrapper>
      )}
      <ContentContainer>
        <MetaAdsContent {...data} paymentState={payment?.state} />

        <InfoContainer>
          {isPerformanceOverviewVisible && (
            <PerformanceOverview
              {...data}
              chartData={chartData}
              isChartVisible={isChartVisible}
            />
          )}
          {isInfoBlockVisible && (
            <InfoBlock
              campaignStatus={data?.status}
              campaignId={Number(campaignID)}
              payment={payment}
              onStartCampaignClick={onStartCampaignClick}
            />
          )}
        </InfoContainer>
      </ContentContainer>
    </PageContainer>
  )
}
